import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Globe, Shield } from 'lucide-react';

const HeritagePercentageBreakdown = ({ surnameResults }) => {
  // Calculate country origin percentages
  const calculateCountryPercentages = () => {
    if (!surnameResults || surnameResults.length === 0) return [];
    
    const countryCount = {};
    surnameResults.forEach(result => {
      const country = result.country_origin || 'Unknown';
      countryCount[country] = (countryCount[country] || 0) + 1;
    });
    
    const total = surnameResults.length;
    return Object.entries(countryCount)
      .map(([country, count]) => ({
        country,
        percentage: ((count / total) * 100).toFixed(1),
        count
      }))
      .sort((a, b) => b.percentage - a.percentage);
  };

  // Calculate Germanic tribe percentages
  const calculateGermanicPercentages = () => {
    if (!surnameResults || surnameResults.length === 0) return [];
    
    const tribeCount = {};
    const germanicSurnames = surnameResults.filter(result => 
      result.germanic_tribe && result.germanic_tribe.tribe_name
    );
    
    if (germanicSurnames.length === 0) return [];
    
    germanicSurnames.forEach(result => {
      const tribe = result.germanic_tribe.tribe_name;
      tribeCount[tribe] = (tribeCount[tribe] || 0) + 1;
    });
    
    const total = surnameResults.length;
    return Object.entries(tribeCount)
      .map(([tribe, count]) => ({
        tribe,
        percentage: ((count / total) * 100).toFixed(1),
        count
      }))
      .sort((a, b) => b.percentage - a.percentage);
  };

  const countryPercentages = calculateCountryPercentages();
  const germanicPercentages = calculateGermanicPercentages();

  const getCountryFlag = (country) => {
    const flagMap = {
      'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      'United Kingdom': '🇬🇧',
      'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      'Wales': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
      'Ireland': '🇮🇪',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Portugal': '🇵🇹',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Poland': '🇵🇱',
      'Russia': '🇷🇺',
      'Greece': '🇬🇷'
    };
    return flagMap[country] || '🌍';
  };

  const getTribeIcon = (tribe) => {
    // Return different icons for different tribes
    const iconMap = {
      'Norse': '⚔️',
      'Scandinavian': '⚔️',
      'Viking': '⚔️',
      'Anglo-Saxon': '🛡️',
      'Anglo-Saxons': '🛡️',
      'Goths': '⚡',
      'Franks': '👑',
      'Lombards': '🏰',
      'Alemanni': '🗡️',
      'Vandals': '🏴',
      'Dutch': '🌷',
      'Frisian': '⛵',
      'Early Germans': '🔱'
    };
    
    for (const [key, icon] of Object.entries(iconMap)) {
      if (tribe.includes(key)) return icon;
    }
    return '⚔️';
  };

  return (
    <div className="space-y-6">
      {/* Germanic Origins Breakdown */}
      {germanicPercentages.length > 0 && (
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center text-xl">
              <Shield className="mr-2 h-6 w-6 text-purple-600" />
              Germanic Tribal Origins
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              {germanicPercentages.reduce((sum, item) => sum + parseFloat(item.percentage), 0).toFixed(1)}% 
              of your family surnames have Germanic tribe connections
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {germanicPercentages.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-800 flex items-center gap-2">
                      <span className="text-2xl">{getTribeIcon(item.tribe)}</span>
                      {item.tribe}
                    </span>
                    <span className="text-lg font-bold text-purple-700">{item.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-pink-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600">
                    {item.count} surname{item.count !== 1 ? 's' : ''} linked to {item.tribe} heritage
                  </p>
                </div>
              ))}
            </div>
            
            {/* Additional Germanic Info */}
            <div className="mt-6 p-4 bg-purple-100 rounded-lg border border-purple-300">
              <p className="text-sm text-purple-900">
                <strong>Germanic Heritage:</strong> Your family tree includes surnames with roots in ancient 
                Germanic tribes that shaped European history. These tribes migrated across Europe between 
                300-1000 CE, establishing kingdoms and influencing modern European cultures.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Show message if no Germanic connections found */}
      {germanicPercentages.length === 0 && (
        <Card className="bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200">
          <CardContent className="pt-6 text-center">
            <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 text-lg font-medium">No Germanic Tribal Connections Found</p>
            <p className="text-gray-500 text-sm mt-2">
              The analyzed surnames do not show direct connections to Germanic tribes. 
              This could indicate heritage from other European regions or non-Germanic origins.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HeritagePercentageBreakdown;
